import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ExternalLink, LogOut, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useCampusAuth } from "@/contexts/CampusAuth";
import { Button } from "@/components/ui/button";
import { formatApiErrorDetail } from "@/lib/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CampusProfil() {
  const { user, logout, refresh } = useCampusAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [busy, setBusy] = useState(false);

  // Load subscription status once
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/subscriptions/status`, { withCredentials: true });
        if (active) setStatus(data);
      } catch {
        // silently ignore – user might not have a subscription yet
      }
      try {
        const { data } = await axios.get(`${API}/subscriptions/tiers`);
        if (active) setTiers((data?.tiers || []).filter((t) => ["pflege_ki_kompakt", "pflege_ki_team"].includes(t.slug)));
      } catch {
        // tiers optional
      }
    })();
    return () => { active = false; };
  }, []);

  if (!user) return null;

  const tier = status?.tier || user.tier || "Helden-Basic";
  const subStatus = status?.subscription_status;
  const hasSub = !!status?.stripe_customer_id && subStatus && subStatus !== "canceled";

  const openPortal = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/subscriptions/portal`,
        { return_url: window.location.origin + "/campus/app/profil" },
        { withCredentials: true },
      );
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Portal konnte nicht geöffnet werden.");
    } finally {
      setBusy(false);
    }
  };

  const startUpgrade = async (selectedTier) => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/subscriptions/checkout`,
        { tier: selectedTier, origin_url: window.location.origin },
        { withCredentials: true },
      );
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Checkout fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const periodEnd = status?.current_period_end
    ? new Date(status.current_period_end).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" })
    : null;

  return (
    <div className="space-y-12" data-testid="campus-profil">
      <header className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-terra" />
            <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra">
              Mein Profil · MMXXVI
            </span>
          </div>
          <h1 className="font-magazine text-sand-800 text-3xl sm:text-[2.6rem] font-medium leading-[1.1]">
            {user.name}
          </h1>
          <p className="mt-3 font-magazine italic text-sand-600 text-[1.05rem]">
            {user.role || "Pflege-Profi"}{user.organisation ? ` · ${user.organisation}` : ""}
          </p>
        </div>
        <button
          onClick={handleLogout}
          data-testid="campus-logout"
          className="inline-flex items-center gap-2 rounded-full border-[1.5px] border-terra/60 bg-transparent px-6 h-10 text-[12px] uppercase tracking-[0.18em] font-semibold text-terra hover:bg-terra hover:text-cream-50 transition-colors"
        >
          <LogOut className="h-3.5 w-3.5" strokeWidth={1.6} />
          Abmelden
        </button>
      </header>

      {/* Impressum-Zeile */}
      <section className="grid sm:grid-cols-3 border-y border-sand-800/15 py-8">
        {[
          ["Mitglieds-Tier", tier, "campus-profil-tier"],
          ["Mitglied seit", user.created_at ? new Date(user.created_at).toLocaleDateString("de-DE", { month: "long", year: "numeric" }) : "—", null],
          ["Status", subStatus === "active" ? "Aktiv" :
            subStatus === "trialing" ? "Im Trial" :
            subStatus === "past_due" ? "Zahlung offen" :
            subStatus === "canceled" ? "Beendet" : "Ohne Abo", null],
        ].map(([label, value, tid], i) => (
          <div key={label} className={`text-center px-4 ${i > 0 ? "sm:border-l border-saffron-dark/40" : ""} ${i > 0 ? "mt-6 sm:mt-0" : ""}`}>
            <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-saffron-dark">
              {label}
            </div>
            <div className="mt-2 font-magazine text-sand-800 text-[1.3rem]" data-testid={tid || undefined}>
              {value}
            </div>
          </div>
        ))}
      </section>

      {/* Subscription block */}
      <section data-testid="campus-profil-subscription">
        <h2 className="font-magazine text-sand-800 text-[1.7rem] leading-tight mb-2">
          Mein <em className="italic text-terra">Abo</em>.
        </h2>
        <p className="text-sand-600 text-[14px] mb-7 max-w-xl">
          Die Pflege-KI Assistenz mit digitaler Patientenmappe, QPR-26 und Team-Funktionen — der einzige Unterschied zwischen den Tarifen ist die Teamgröße.
        </p>
        {hasSub ? (
          <div className="bg-cream-100 border border-sand-800/15 p-7">
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-saffron-dark">
                  Aktiver Plan
                </div>
                <div className="mt-2 font-magazine text-sand-800 text-[1.5rem]">
                  {tier}
                </div>
                {periodEnd && (
                  <p className="mt-2 font-magazine italic text-sand-600 text-[13.5px]">
                    {status?.cancel_at_period_end
                      ? `Endet am ${periodEnd} · keine weitere Abbuchung.`
                      : `Nächste Abbuchung: ${periodEnd}`}
                  </p>
                )}
              </div>
              <Button
                onClick={openPortal}
                disabled={busy}
                data-testid="campus-profil-portal"
                variant="outline"
                className="rounded-full border-[1.5px] border-terra text-terra hover:bg-terra hover:text-cream-50 bg-transparent h-11 px-6 text-[12px] uppercase tracking-[0.18em] font-semibold transition-colors"
              >
                <span className="inline-flex items-center gap-2">
                  Abo verwalten
                  <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.6} />
                </span>
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5">
            {(tiers.length ? tiers : [
              { slug: "pflege_ki_kompakt", tier: "Pflege-KI Kompakt", amount_cents: 19900 },
              { slug: "pflege_ki_team", tier: "Pflege-KI Team", amount_cents: 34900 },
            ]).map((t) => (
              <UpgradeCard
                key={t.slug}
                title={t.tier}
                price={`${Math.round((t.amount_cents || 0) / 100)} € / Monat`}
                note={t.slug === "pflege_ki_team"
                  ? "für Pflegedienste ab 11 Mitarbeiter · unbegrenzte Sub-Accounts"
                  : "für Pflegedienste bis 10 Mitarbeiter · 1 PDL + 10 Sub-Accounts"}
                onClick={() => startUpgrade(t.slug)}
                busy={busy}
                testId={`campus-upgrade-${t.slug}`}
              />
            ))}
          </div>
        )}
      </section>

      {/* Account details */}
      <section>
        <h2 className="font-magazine text-sand-800 text-[1.7rem] leading-tight mb-6">
          <em className="italic text-terra">Account</em>.
        </h2>
        <dl className="divide-y divide-cream-300 border-y border-cream-300">
          {[
            ["E-Mail", user.email],
            ["Organisation", user.organisation || "—"],
            ["Rolle", user.role || "—"],
            ["E-Mail verifiziert", user.email_verified ? "Ja" : "Nein"],
          ].map(([k, v]) => (
            <div key={k} className="grid sm:grid-cols-[200px_1fr] gap-3 py-4">
              <dt className="text-[12.5px] uppercase tracking-[0.18em] font-semibold text-sand-500">{k}</dt>
              <dd className="text-[14.5px] text-sand-800">{v}</dd>
            </div>
          ))}
        </dl>
        <button
          onClick={async () => {
            try {
              await axios.post(`${API}/onboarding/reset`, {}, { withCredentials: true });
              await refresh?.();
              toast.success("Willkommens-Tour startet beim nächsten Dashboard-Besuch erneut.");
            } catch {
              toast.error("Tour konnte nicht zurückgesetzt werden.");
            }
          }}
          data-testid="campus-profil-restart-tour"
          className="mt-6 text-[13px] text-sand-600 hover:text-terra underline-offset-4 hover:underline"
        >
          Willkommens-Tour erneut starten →
        </button>
      </section>
    </div>
  );
}

function UpgradeCard({ title, price, note, onClick, busy, testId }) {
  return (
    <button
      onClick={onClick}
      disabled={busy}
      data-testid={testId}
      className="text-left bg-cream-100 border border-sand-800/15 p-7 hover:border-terra transition-colors disabled:opacity-60 group"
    >
      <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-saffron-dark">{title}</div>
      <div className="mt-3 font-magazine text-sand-800 text-[1.9rem] leading-none">{price}</div>
      <p className="mt-3 font-magazine italic text-sand-600 text-[14.5px]">{note}</p>
      <div className="mt-6 inline-flex items-center gap-1.5 text-[12px] uppercase tracking-[0.18em] text-terra font-semibold group-hover:gap-3 transition-all">
        {busy ? "Wird vorbereitet…" : "Upgrade starten"}
        {!busy && <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.8} />}
      </div>
    </button>
  );
}
